/**
 * AI Chat Component
 * 
 * Interactive chat interface with:
 * - Message bubbles (user/assistant)
 * - Simulated typing effect
 * - Loading states
 * - Framer Motion animations
 */

import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Trash2, Sparkles } from 'lucide-react';
import { useAI, ChatMessage } from '../hooks/useAI';

// Animation variants
const messageVariants = {
  hidden: { opacity: 0, x: 20, scale: 0.95 },
  visible: { 
    opacity: 1, 
    x: 0, 
    scale: 1,
    transition: { type: 'spring', stiffness: 400, damping: 30 }
  },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.15 } }
};

const userMessageVariants = {
  hidden: { opacity: 0, x: -20, scale: 0.95 },
  visible: { 
    opacity: 1, 
    x: 0, 
    scale: 1,
    transition: { type: 'spring', stiffness: 400, damping: 30 }
  },
};

function fallbackModelName(model: string) {
  if (model === 'gpt-5.5-pro') return 'GPT-5.5 Pro';
  if (model === 'gpt-5.5') return 'GPT-5.5';
  return model;
}

export function AIChat() {
  const {
    messages,
    isTyping,
    sendMessage,
    clearChat,
    isSendingMessage,
    isConfigured,
    models,
  } = useAI();

  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [typingSeconds, setTypingSeconds] = useState(0);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!isTyping) {
      setTypingSeconds(0);
      return;
    }
    setTypingSeconds(0);
    const t = setInterval(() => setTypingSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [isTyping]);

  const lastAssistantModel =
    [...messages].reverse().find((m) => m.role === 'assistant' && m.model)?.model ??
    models.find((m) => m.isDefault)?.id ??
    'gpt-5.5';

  const friendlyModelName =
    models.find((m) => m.id === lastAssistantModel)?.name ??
    fallbackModelName(lastAssistantModel);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isSendingMessage) {
      sendMessage(input);
      setInput('');
    }
  };

  if (!isConfigured) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <Bot className="w-12 h-12 text-gray-400 mb-4" />
        <p className="text-gray-500 dark:text-gray-400 text-center">
          AI chat not available. Contact support to enable.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Bot className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900 dark:text-white">
              AI Trade Analyst
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Ask about your trading performance
            </p>
            <p className="text-xs text-indigo-500 dark:text-indigo-400">
              Using {friendlyModelName}
            </p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Clear chat"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center h-full text-center"
          >
            <div className="w-16 h-16 mb-4 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-2xl flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Ask me anything
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
              I can analyze your trades, identify patterns, and suggest improvements.
            </p>
            <div className="mt-6 space-y-2">
              <SuggestionButton onClick={() => setInput('What are my best performing tickers?')}>
                What are my best performing tickers?
              </SuggestionButton>
              <SuggestionButton onClick={() => setInput('Should I be worried about any patterns?')}>
                Should I be worried about any patterns?
              </SuggestionButton>
              <SuggestionButton onClick={() => setInput('How can I improve my win rate?')}>
                How can I improve my win rate?
              </SuggestionButton>
              <SuggestionButton onClick={() => setInput('Show me trades from my latest algorithm')}>
                Show me trades from my latest algorithm
              </SuggestionButton>
            </div>
          </motion.div>
        )}

        <AnimatePresence mode="popLayout">
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
        </AnimatePresence>

        {/* Typing Indicator */}
        <AnimatePresence>
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex items-start gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <Bot className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex gap-1">
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.4, repeat: Infinity, delay: 0 }}
                    className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full"
                  />
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.4, repeat: Infinity, delay: 0.2 }}
                    className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full"
                  />
                  <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.4, repeat: Infinity, delay: 0.4 }}
                    className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full"
                  />
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Thinking… {typingSeconds}s
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your trading..."
            disabled={isSendingMessage}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:opacity-50"
          />
          <motion.button
            type="submit"
            disabled={!input.trim() || isSendingMessage}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl transition-colors disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </motion.button>
        </div>
      </form>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  const [displayedContent, setDisplayedContent] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  // Typing effect for assistant messages
  useEffect(() => {
    if (!isUser && displayedContent !== message.content) {
      setIsAnimating(true);
      let index = 0;
      const interval = setInterval(() => {
        if (index < message.content.length) {
          setDisplayedContent(message.content.slice(0, index + 1));
          index++;
        } else {
          clearInterval(interval);
          setIsAnimating(false);
        }
      }, 15); // 15ms per character for smooth typing
      return () => clearInterval(interval);
    } else if (isUser) {
      setDisplayedContent(message.content);
    }
  }, [message.content, isUser]);

  return (
    <motion.div
      variants={isUser ? userMessageVariants : messageVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout
      className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
    >
      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
        isUser 
          ? 'bg-gray-200 dark:bg-gray-700' 
          : 'bg-indigo-100 dark:bg-indigo-900/30'
      }`}>
        {isUser ? (
          <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        ) : (
          <Bot className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        )}
      </div>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
        isUser 
          ? 'bg-indigo-600 text-white rounded-tr-sm' 
          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-tl-sm'
      }`}>
        <p className="text-sm whitespace-pre-wrap">
          {displayedContent}
          {isAnimating && (
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="inline-block w-0.5 h-4 bg-current ml-0.5 align-middle"
            />
          )}
        </p>
        <p className={`text-xs mt-1 ${
          isUser 
            ? 'text-indigo-200' 
            : 'text-gray-400 dark:text-gray-500'
        }`}>
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {!isUser && message.model && (
            <span className="ml-2">
              via {fallbackModelName(message.model)}
            </span>
          )}
        </p>
      </div>
    </motion.div>
  );
}

function SuggestionButton({ 
  children, 
  onClick 
}: { 
  children: React.ReactNode; 
  onClick: () => void;
}) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
    >
      {children}
    </motion.button>
  );
}
